import React, { useState } from "react";
import { motion } from "framer-motion";
import { StaffMember, updateStaffMember } from "@/services/staffService";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MapPin, Building, Pencil, Phone, Mail, Calendar, UserPlus, Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import EditStaffDialog from "./dialogs/EditStaffDialog";
import LocationAssignmentDialog from "./LocationAssignmentDialog";
import { toast } from "sonner";
import { useStaffClassAssignments } from "@/hooks/useStaffClassAssignments";
import { useQuery } from "@tanstack/react-query";
import { useStaffLocations } from "@/hooks/useStaffLocations";
import { StaffInvitationService } from "@/services/staffInvitationService";
import { useAuth } from "@/hooks/useAuth";
import { fetchOrganizationData } from "@/utils/auth/organizationData";
import InviteStatusBadge from '@/components/families/InviteStatusBadge';

interface StaffCardProps {
  staff: StaffMember;
  onUpdate: () => void;
}

const getRoleBadgeVariant = (role?: string): "default" | "outline" | "secondary" | "destructive" => {
  switch (role) {
    case "admin":
      return "destructive";
    case "instructor":
      return "default";
    case "manager":
      return "secondary";
    default:
      return "outline";
  }
};

const StaffCard: React.FC<StaffCardProps> = ({ staff, onUpdate }) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  
  const { data: classAssignments = [] } = useStaffClassAssignments(staff.id);
  const { assignments = [] } = useStaffLocations(staff.id);
  const { user, organizationId } = useAuth();

  // Get assigned locations count
  const assignedLocationsCount = assignments.filter(a => a.assigned).length;

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'U';
    const last = lastName?.charAt(0) || 'N';
    return `${first}${last}`.toUpperCase();
  };

  const handleStatusChange = async (checked: boolean) => {
    setIsUpdating(true);
    
    try {
      const updatedStaff = await updateStaffMember(staff.id, {
        ...staff,
        is_active: checked
      });
      
      if (updatedStaff) {
        toast.success(`${staff.first_name} ${staff.last_name} is now ${checked ? 'active' : 'inactive'}`);
        onUpdate();
      } else {
        toast.error("Failed to update staff status");
      }
    } catch (error) {
      console.error("Error updating staff status:", error);
      toast.error("There was a problem updating the staff status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInviteWithoutEmail = () => {
    toast.info("Please add an email address first", {
      description: "You need to add an email address before sending an invite"
    });
    setIsEditDialogOpen(true);
  };

  const handleSendInvite = async () => {
    if (!staff.email) {
      toast.error("Cannot send invite: No email address found for this staff member");
      return;
    }

    if (!organizationId) {
      toast.error("Cannot send invite: Organization not found");
      return;
    }

    setIsInviting(true);
    
    try {
      // Get organization name
      let organizationName = undefined;
      try {
        const orgData = await fetchOrganizationData(organizationId);
        organizationName = orgData?.name;
      } catch (orgErr) {
        console.warn('Could not fetch organization name for invite:', orgErr);
      }

      const result = await StaffInvitationService.sendStaffInvite(staff.email, {
        staff_id: staff.id,
        staffName: `${staff.first_name} ${staff.last_name}`,
        role: staff.role || 'staff',
        organization_id: organizationId,
        organizationName,
        firstName: staff.first_name,
        lastName: staff.last_name,
        created_by: user?.id
      });

      if (result.success) {
        // Check if this was a resend (staff already had email)
        const isResend = staff.email;
        toast.success(isResend ? "Invite resent successfully" : "Invite sent successfully", {
          description: `An invite has been ${isResend ? 'resent' : 'sent'} to ${staff.email}`
        });
        onUpdate(); // Refresh the staff data
      } else {
        toast.error("Failed to send invite", {
          description: result.error || "There was an error sending the invite"
        });
      }
    } catch (error) {
      console.error("Error sending staff invite:", error);
      toast.error("Failed to send invite", {
        description: "There was an error sending the invite. Please try again."
      });
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      className="h-full min-w-[340px]"
    >
      <Card className={`h-full min-w-[340px] p-6 flex flex-col space-y-6 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 hover:border-primary/30 group ${!staff.is_active ? 'opacity-60' : ''}`}>
        {/* Header: Avatar, Name, Role, Status */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="h-14 w-14">
              <AvatarImage src={staff.profile_photo_url || ''} alt={`${staff.first_name} ${staff.last_name}`} />
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700 text-lg">
                {getInitials(staff.first_name, staff.last_name)}
              </AvatarFallback>
            </Avatar>
            {staff.role === 'admin' && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <Shield className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xl font-bold leading-tight group-hover:text-primary transition-colors duration-200 truncate">
              {staff.first_name || 'Unknown'} {staff.last_name || 'User'}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getRoleBadgeVariant(staff.role)} className="text-xs">
                {staff.role || 'Staff'}
              </Badge>
              {/* Status badge */}
              {staff.user_id ? (
                <InviteStatusBadge status="accepted" />
              ) : staff.email ? (
                <InviteStatusBadge status="sent" />
              ) : (
                <InviteStatusBadge status="pending" />
              )}
            </div>
          </div>
          <Switch 
            checked={staff.is_active} 
            disabled={isUpdating}
            onCheckedChange={handleStatusChange}
          />
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="flex-1 bg-blue-50 rounded-lg p-4 text-center transition-all duration-200 hover:bg-blue-100 hover:scale-105 cursor-pointer">
            <div className="flex items-center justify-center gap-1 mb-1">
              <MapPin className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-lg font-semibold text-blue-700">{assignedLocationsCount}</div>
            <div className="text-xs text-blue-600">
              {assignedLocationsCount === 1 ? 'Location' : 'Locations'}
            </div>
          </div>
          <div className="flex-1 bg-purple-50 rounded-lg p-4 text-center transition-all duration-200 hover:bg-purple-100 hover:scale-105 cursor-pointer">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Calendar className="h-4 w-4 text-purple-500" />
            </div>
            <div className="text-lg font-semibold text-purple-700">{classAssignments.length}</div>
            <div className="text-xs text-purple-600">
              {classAssignments.length === 1 ? 'Class' : 'Classes'}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-500" />
          <span className="truncate">{staff.email}</span>
        </div>
        {staff.phone && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Phone className="h-4 w-4 text-green-500" />
            <span>{staff.phone}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-auto">
          {/* Primary action: Initial Invite (no email = not invited yet) */}
          {!staff.email && (
            <Button 
              variant="default" 
              size="lg" 
              className="w-full font-semibold text-base py-2"
              onClick={handleInviteWithoutEmail}
              disabled={isInviting}
            >
              <UserPlus className="h-5 w-5 mr-2" />
              Invite to Platform
            </Button>
          )}
          
          {/* Resend Invite action (has email but no user_id = already invited) */}
          {staff.email && !staff.user_id && (
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full font-semibold text-base py-2 border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={handleSendInvite}
              disabled={isInviting}
            >
              <UserPlus className="h-5 w-5 mr-2" />
              {isInviting ? 'Sending...' : 'Resend Invite'}
            </Button>
          )}
          {/* Secondary actions: Locations and Edit */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => setIsLocationDialogOpen(true)}
            >
              <MapPin className="h-4 w-4 mr-1" /> 
              Locations
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => setIsEditDialogOpen(true)}
            >
              <Pencil className="h-4 w-4 mr-1" /> 
              Edit
            </Button>
          </div>
        </div>

        {/* Dialogs */}
        <EditStaffDialog
          staff={staff}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onUpdate={onUpdate}
        />
        <LocationAssignmentDialog
          staffId={staff.id}
          staffName={`${staff.first_name} ${staff.last_name}`}
          open={isLocationDialogOpen}
          onOpenChange={setIsLocationDialogOpen}
        />
      </Card>
    </motion.div>
  );
};

export default StaffCard;
//comment
